import { Badge, cn } from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import type { PlayerDtoContractStatus } from '@/api/generated/model';

/**
 * A player's positions as pills: the primary filled, secondaries outlined.
 * The table is scanned by position more than by anything except name, so the
 * primary has to stand out at a glance.
 */
export function PositionPills({
  primary,
  secondary = [],
  className,
}: {
  primary: string;
  secondary?: readonly string[];
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      <Badge shape="pill" className="px-2">
        {primary}
      </Badge>
      {secondary.map((position) => (
        <Badge key={position} shape="pill" variant="outline" className="px-2">
          {position}
        </Badge>
      ))}
    </div>
  );
}

const CONTRACT_VARIANTS = {
  active: 'secondary',
  expiring: 'destructive',
  expired: 'outline',
  none: 'outline',
} as const satisfies Record<PlayerDtoContractStatus, string>;

export function ContractStatusBadge({ status }: { status: PlayerDtoContractStatus }) {
  const { t } = useTranslation();

  return (
    <Badge
      variant={CONTRACT_VARIANTS[status]}
      className={cn(status === 'expired' && 'text-muted-foreground')}
    >
      {t(`players.contractStatus.${status}`)}
    </Badge>
  );
}
